import FilterButton from "@common/components/Button/FilterButton";
import Grid from '@mui/material/Grid2';
import { CSVMenuProps } from "@common/components/CSV/CSVMenu";
import FilteringBar from "@common/components/FilteringBar";
import UploadDialog, { UploadType } from "@common/components/Logo/UploadDialog";
import Typography from "@common/components/Typography";
import {
  getCurrentSizeProfile,
  responsiveFontSize,
} from "@common/components/Typography/style";
import { FiltersTypeImpl, useUserFilter } from "@common/utils/filters";
import { canBulkChange, canEditGlobalConfiguration } from "@common/utils/user";
import { Box, Divider, Skeleton, Stack } from "@mui/material";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { LEFT_FIXED_SIZE } from "../ListView/colDefs";

type DeviceCatalogFilterbarProps = {
  total: number;
  loading: boolean;
  filters: FilterDefinition[];
  setFilters: (filters: FilterDefinition[]) => void;
  active: boolean;
  setActive: Dispatch<SetStateAction<boolean>>;
  accountId?: string;
  csvExportProps?: CSVMenuProps;
  refetch: () => void;
};

const DeviceCatalogFilterbar = (props: DeviceCatalogFilterbarProps) => {
  const {
    total,
    loading,
    filters,
    setFilters,
    active,
    setActive,
    accountId,
    csvExportProps,
    refetch,
  } = props;
  const { t } = useTranslation();
  const [user] = useAuthenticatedUser();
  const canEdit = canEditGlobalConfiguration(user);
  const canBulk = canBulkChange(user);
  const [openUploadDialog, setOpenUploadDialog] = useState<boolean>(false);
  const [, setCatalogFilters] = useUserFilter("deviceCatalogFilters");

  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      sx={(theme) => ({
        fontSize: getCurrentSizeProfile().small,
        ...responsiveFontSize(theme, "small"),
        height: "3em",
        paddingTop: "0.5em",
        paddingBottom: "0.5em",
        alignItems: "center",
      })}
    >
      <Box
        sx={(theme) => ({
          width: LEFT_FIXED_SIZE,
          minWidth: LEFT_FIXED_SIZE,
          fontSize: getCurrentSizeProfile().small,
          ...responsiveFontSize(theme, "small"),
          height: "3em",
          padding: "0.5em",
        })}
      >
        <Typography
          sx={{
            lineHeight: 2,
            textAlign: "center",
          }}
        >
          {loading ? (
            <Skeleton animation="wave"></Skeleton>
          ) : (
            `${`${total ?? 0}`} ${t(
              total !== 0 && total > 1 ? "Models" : "Model"
            )}`
          )}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Stack
        direction="row"
        sx={{
          flex: 1,
          alignItems: "center",
          pl: 2,

          "& > *": {
            mr: 2,
            "&.MuiAutocomplete-root": { width: 1 / 5 },
            "&.MuiFormControl-root": { width: 1 / 5, mr: 2 },
          },
        }}
      >
        {/* will be remove when the BE has added the “active” filter */}
        <Grid>
          <FilterButton
            disabled={loading}
            onClick={() => setActive(!active)}
            selected={active}
            value="active"
          >
            {t("Active")}
          </FilterButton>
        </Grid>
        <FilteringBar
          filters={filters}
          setFilters={setFilters}
          type={FiltersTypeImpl.DeviceCatalog}
          refetch={refetch}
          csvExportProps={csvExportProps}
          loading={loading}
          hideColumnButton
          disableAddButton={Boolean(accountId) && !canEdit}
          onImport={canEdit ? () => setOpenUploadDialog(true) : undefined}
          setLocalStorageFilter={setCatalogFilters}
        />
        <UploadDialog
          open={openUploadDialog}
          onClose={() => setOpenUploadDialog(false)}
          refetch={refetch}
          uploadType={UploadType.Catalog}
          acceptedFiles={{ "application/zip": [".zip"] }}
          title={t("Import Catalog")}
        />
      </Stack>
    </Grid>
  );
};

export default DeviceCatalogFilterbar;
